/**
 * Gets or creates a nested value at the current path level for recursive operations.
 * If the existing value is an object or array, a copy is returned to maintain immutability.
 * Otherwise, a new object or array is created based on whether the next path part is numeric.
 *
 * @param obj - The current object or array being traversed.
 * @param firstPart - The current path part (property name or array index).
 * @param restParts - The remaining path parts.
 * @returns A copy of the nested value to recurse into, either copied or newly created.
 */
export const getNestedValue = <T extends Record<string, unknown> | unknown[]>(
  obj: T,
  firstPart: string,
  restParts: string[],
): Record<string, unknown> | unknown[] => {
  const existingValue = obj[firstPart as keyof T];
  const nested =
    (typeof existingValue === "object" || Array.isArray(existingValue)) && existingValue !== null
      ? Array.isArray(existingValue)
        ? ([...existingValue] as unknown[])
        : ({ ...existingValue } as Record<string, unknown>)
      : /^\d+$/.test(restParts[0])
        ? []
        : {};
  return nested;
};
